import React, { RefObject } from 'react'
import { NumberFormatValues } from 'react-number-format'
import styled from 'styled-components'
import { mobile } from '../../common/assets/utils/_breakpoints'
import {
  SourceProduct,
  VerificationMethodTypeEnum,
} from '../../services/bff/coordinator/dto/Contracts.dto'
import { IPhoneCommunicationChannel } from '../../services/bff/originations/dto/PhoneVerificationRecord.dto'
import PurchaseInstallmentsOptionsCard, {
  PurchaseConditions,
} from './PurchaseInstallmentsOptionsCard'
import PurchaseResumeCard from './PurchaseResumeCard'
import { PurchaseConfirmationPopup } from './PurchaseConfirmationPopup'
import { ProductPriceRangeInputSection } from '../../cash-register/purchases-old/components/ProductPriceRangeInputSection'
import {
  calculateMaxPrice,
  calculateMaxAmountPlusDownPayment,
} from '../../organisms/ProductSale/components/ProductsHelper'
import { TransactionalCreditEvaluation } from '../../services/bff/transactional-credit/dtos/transactional-credit-evaluation.dto'
import { Product } from '../../organisms/ProductSale/components/ProductsSession'
import { AutoScrollToView } from '../../cash-register/common/AutoScrollToView'
import { tablet } from '../../legacy-lib/design-kit-ui/utils/_breakpoints'

enum LimitCheckStatus {
  WaitingPurchaseInput,
  CheckingLimitAgainstPurchase,
  IsApproved,
  NotApproved,
}

interface SmartphoneSaleProps {
  selectedProduct: Product
  showPriceRangeInputSection: boolean
  onProductPriceRangeInputConfirm: (
    downPayment: number,
    purchaseValueMinusDownPayment: number,
    productPrice: number
  ) => void
  onProductPriceRangeInputValueChange: () => void
}
interface Props {
  purchaseValue: string
  purchaseConditions: PurchaseConditions[]
  couponDiscount: number
  selectedPurchaseCondition: number
  isVerificationDialogOpen: boolean
  isPurchaseConfirmed: boolean
  authorizationStrategy: string
  formattedCellphone: string
  togglePurchaseConditionSelected: (purchaseConditionIdx: number) => void
  onKeyPressTableRow: (index: number, event: React.KeyboardEvent) => void
  openVerificationDialog: () => void
  startVerificationProcess: () => void
  handleKeyPressCancel: (event: React.KeyboardEvent) => void
  cancelPurchase: () => void
  finishPurchase: () => void
  sourceProduct?: string
  downPayment?: number
  purchaseValueMinusDownPayment?: number
  principal: number
  productPrice?: number
  contractId: string
  proposalId: string
  verificationMethod?: VerificationMethodTypeEnum
  transactionalCreditEvaluationId?: string
  productId?: string
  transactionCreditEvaluation?: TransactionalCreditEvaluation
  limitCheckStatus: LimitCheckStatus
  smartphoneSaleProps?: SmartphoneSaleProps
}

export const PurchaseContractOptions: React.FC<Props> = (props) => {
  const {
    purchaseValue,
    purchaseConditions,
    couponDiscount,
    selectedPurchaseCondition,
    isVerificationDialogOpen,
    isPurchaseConfirmed,
    authorizationStrategy,
    formattedCellphone,
    togglePurchaseConditionSelected,
    onKeyPressTableRow,
    openVerificationDialog,
    startVerificationProcess,
    handleKeyPressCancel,
    cancelPurchase,
    finishPurchase,
    sourceProduct,
    downPayment,
    purchaseValueMinusDownPayment,
    principal,
    productPrice,
    contractId,
    proposalId,
    transactionalCreditEvaluationId,
    transactionCreditEvaluation,
    productId,
    verificationMethod,
    limitCheckStatus,
    smartphoneSaleProps
  } = props

  const hasFinancingOptions = purchaseConditions.length > 0
  const selectedInstallment = purchaseConditions.find(
    (condition) => condition.isSelected && condition.isFocused
  )

  return (
    <PurschaseOptionsSection>
      <PurschaseOptionsContainer>
        <PurchaseOptionsCard>
          {smartphoneSaleProps?.showPriceRangeInputSection && (
            <>
              <AutoScrollToView />
              <ProductPriceRangeInputSection
                productPrice={smartphoneSaleProps?.selectedProduct?.price!!}
                maxPrice={
                  calculateMaxPrice(
                    smartphoneSaleProps?.selectedProduct?.price!!,
                    calculateMaxAmountPlusDownPayment(
                      smartphoneSaleProps?.selectedProduct?.maxDownPaymentRate!!,
                      transactionCreditEvaluation?.policy?.maxAmount ?? 0
                    )
                  )
                }
                minPriceRate={smartphoneSaleProps?.selectedProduct?.minPriceRate}
                downPaymentRate={smartphoneSaleProps?.selectedProduct?.maxDownPaymentRate!!}
                onConfirm={smartphoneSaleProps?.onProductPriceRangeInputConfirm}
                onValueChange={smartphoneSaleProps?.onProductPriceRangeInputValueChange}
              />
            </>
          )}
          {limitCheckStatus === LimitCheckStatus.IsApproved && hasFinancingOptions && (
            <>
              <AutoScrollToView />
              <PurchaseInstallmentsOptionsCard
                togglePurchaseConditionSelected={togglePurchaseConditionSelected}
                onKeyPressTableRow={onKeyPressTableRow}
                purchaseConditions={purchaseConditions}
                sourceProduct={sourceProduct}
              />
            </>
          )}
        </PurchaseOptionsCard>
        <PurchaseResumeCardContainer>
          {limitCheckStatus === LimitCheckStatus.IsApproved && selectedInstallment ? (
            <PurchaseResumeCard
              purchaseValue={purchaseValue}
              interestRate={selectedInstallment.interestRate}
              lateInterestRate={selectedInstallment.lateInterestRate}
              installmentValueWithoutBonification={
                selectedInstallment.installmentValueWithoutBonification
              }
              totalPurchaseValue={selectedInstallment.totalPurchaseValue}
              iof={selectedInstallment.iof}
              installments={selectedInstallment.installments}
              installmentValue={selectedInstallment.installmentValue}
              installmentValueWithoutInterest={selectedInstallment.installmentValueWithoutInterest}
              firstInstallmentDueDate={selectedInstallment.firstInstallmentDueDate}
              couponDiscount={couponDiscount}
              selectedPurchaseCondition={selectedPurchaseCondition}
              openVerificationDialog={openVerificationDialog}
              customerFeeRate={selectedInstallment.customerFeeRate}
              customerFeeValue={selectedInstallment.customerFeeValue}
              dueDateRule={selectedInstallment.dueDateRule}
              sourceProduct={sourceProduct}
              downPayment={downPayment}
              purchaseValueMinusDownPayment={purchaseValueMinusDownPayment}
              principal={principal}
              productPrice={productPrice}
            />
          ) : null}
        </PurchaseResumeCardContainer>
      </PurschaseOptionsContainer>
      <PurchaseConfirmationPopup
        purchaseConditions={purchaseConditions}
        selectedPurchaseCondition={selectedPurchaseCondition}
        isVerificationDialogOpen={isVerificationDialogOpen}
        isPurchaseConfirmed={isPurchaseConfirmed}
        authorizationStrategy={authorizationStrategy}
        formattedCellphone={formattedCellphone}
        startVerificationProcess={startVerificationProcess}
        handleKeyPressCancel={handleKeyPressCancel}
        cancelPurchase={cancelPurchase}
        finishPurchase={finishPurchase}
        sourceProduct={sourceProduct}
        contractId={contractId}
        proposalId={proposalId}
        transactionalCreditEvaluationId={transactionalCreditEvaluationId}
        productId={productId}
        verificationMethod={verificationMethod}
      />
    </PurschaseOptionsSection>
  )
}

export default PurchaseContractOptions

const PurschaseOptionsSection = styled.div`
  width: 100%;
  background-color: #eff4f0;
  border-radius: 20px;
  justify-content: space-around;

  @media (max-width: ${mobile}) {
    justify-content: center;
  }
`

const PurschaseOptionsContainer = styled.div`
  display: flex;
  justify-content: center;

  @media (max-width: ${tablet}) {
    width: 100%;
    flex-wrap: wrap;
  }
`

const PurchaseOptionsCard = styled.div`
  width: 100%;
  display: block;
  justify-content: center;
`

const PurchaseResumeCardContainer = styled.div`
  width: 100%;
  height: 70%;
  display: block;
  justify-content: center;
`
